import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../components/Layout/Layout';
import { adminApi } from '../config/axios';
import { useDocumentTitle } from '../config/utils';

const POLL_MS = 20000;

// Friendly labels + descriptions per service key returned by GET /admin/health.
const META = {
  database:           { label: 'Database',        sub: 'Cloud SQL (MySQL)' },
  redis:              { label: 'Cache / Redis',   sub: 'Memorystore' },
  scheduler:          { label: 'Scheduler',       sub: 'Per-minute tick' },
  wall_splits_worker: { label: 'Split Worker',    sub: 'ffmpeg encode queue' },
  queue:              { label: 'Queue',           sub: 'wall_splits backlog' },
  renditions:         { label: 'Rendition Pipeline', sub: 'split / normalize' },
  video_walls:        { label: 'Video Walls',     sub: 'live playback' },
};

const STATUS = {
  ok:       { dot: 'bg-emerald-500', text: 'text-emerald-400', ring: 'border-emerald-500/30', label: 'Operational' },
  degraded: { dot: 'bg-amber-500',   text: 'text-amber-400',   ring: 'border-amber-500/40',   label: 'Degraded' },
  down:     { dot: 'bg-red-500',     text: 'text-red-400',     ring: 'border-red-500/50',     label: 'Down' },
};
const sx = (s) => STATUS[s] || STATUS.down;

export default function Health() {
  useDocumentTitle('System Health');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);
  const timer = useRef(null);

  const load = useCallback(() => {
    adminApi.get('/health')
      .then((res) => { setReport(res.data); setError(null); })
      .catch((e) => setError(e?.response?.status === 403 ? 'Not authorized' : 'Could not reach the health endpoint'))
      .finally(() => { setLoading(false); setFetchedAt(new Date()); });
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(load, POLL_MS);
    return () => clearInterval(timer.current);
  }, [load]);

  const overall = report?.status || 'down';
  const services = report?.services || {};
  const banner = sx(overall);

  return (
    <Layout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">System Health</h1>
          <p className="text-text-muted text-sm">Live infrastructure status · auto-refreshes every 20s</p>
        </div>
        <div className="text-right">
          <button onClick={load} className="btn-icon" title="Refresh now">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          </button>
          {fetchedAt && <p className="text-text-muted text-xs mt-1">Updated {fetchedAt.toLocaleTimeString()}</p>}
        </div>
      </div>

      {/* Overall banner */}
      <div className={`flex items-center gap-3 rounded-xl border ${banner.ring} bg-bg-card px-5 py-4 mb-6`}>
        <span className={`relative flex h-3 w-3`}>
          {overall !== 'ok' && <span className={`absolute inline-flex h-full w-full rounded-full ${banner.dot} opacity-60 animate-ping`} />}
          <span className={`relative inline-flex rounded-full h-3 w-3 ${banner.dot}`} />
        </span>
        <div>
          <p className="text-white font-semibold">
            {loading ? 'Checking…' : overall === 'ok' ? 'All systems operational'
              : overall === 'degraded' ? 'Degraded — some checks are warning' : 'Outage — one or more services are down'}
          </p>
          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>
      </div>

      {/* Service grid */}
      {loading && !report ? (
        <p className="text-text-muted">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(services).map(([key, s]) => {
            const m = META[key] || { label: key, sub: '' };
            const st = sx(s.status);
            return (
              <div key={key} className={`rounded-xl border ${st.ring} bg-bg-card p-4`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-white font-semibold">{m.label}</p>
                    <p className="text-text-muted text-xs">{m.sub}</p>
                  </div>
                  <span className="flex items-center gap-1.5">
                    <span className={`inline-flex rounded-full h-2.5 w-2.5 ${st.dot}`} />
                    <span className={`text-xs font-medium ${st.text}`}>{st.label}</span>
                  </span>
                </div>
                <p className="text-text-muted text-xs mt-3 break-words">{s.detail}</p>
                {typeof s.latency_ms === 'number' && (
                  <p className="text-text-muted text-[11px] mt-1">latency {s.latency_ms} ms</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
